import type { MessageKey } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `pluralCategory('pt-BR', …)` and never `count === 1`: Brazilian Portuguese puts ZERO in the
 * `one` category, so *0 agente* is correct here and a two-branch check would be wrong on every
 * empty state (D-19).
 */
const plural = (count: number, forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }): string =>
  forms[pluralCategory('pt-BR', count)] ?? forms.other

/**
 * What the systems domain's refusals say, in Brazilian Portuguese (checked against the English
 * shape at compile time).
 *
 * `sistema conectado` (m.) and never *MCP* in a sentence a person reads (PRD-23); `política`
 * (f.), so the participles agree with it — *a política não foi gravada*, *não pode ser
 * removida*. `endpoint`, `token`, `schema`, `slug`, `card` and `link-local` stay English:
 * those are the words a Brazilian administrator reads in every other tool they have open
 * (D-15).
 */
export const systems: Pick<Messages['errors'], Extract<MessageKey, `systems.${string}`>> = {
  // --- a connected system --------------------------------------------------

  'systems.not_found': () => 'Nenhum sistema conectado com esse id está registrado.',
  'systems.name_taken': ({ name }) => `Já existe um sistema conectado chamado “${name}”.`,
  'systems.changed_by_someone_else': () =>
    'Outra pessoa mudou este sistema conectado. Recarregue e refaça sua alteração.',
  'systems.if_match_invalid': () => 'O If-Match precisa ser o número da versão que você leu por último.',
  'systems.url_invalid': () =>
    'Esse endpoint não é uma URL que a plataforma consiga usar. Informe um endereço https:// absoluto e ' +
    'sem credenciais.',
  'systems.url_not_routable': () =>
    'Esse endereço é privado, de loopback ou link-local, e a plataforma não disca para dentro da própria ' +
    'rede. Registre um endpoint acessível de fora.',

  // --- o que mudar quando um sistema não conecta --------------------------
  //
  // A linha de conselho do fluxo de conexão. São dezesseis frases para oito estágios, porque
  // um estágio não é uma frase: só `url_invalid` são cinco problemas com cinco consertos
  // diferentes, e juntar tudo em “falhou” manda todo mundo para a pessoa errada (MCP-025).
  //
  // *sistema conectado* (m.) em todas elas, nunca a sigla (PRD-23). Ficam em inglês: `endpoint`,
  // `token`, `bearer token`, `https`, `HTTP`, `DNS`, `TLS`, `egress`, `proxy reverso`,
  // `link-local`, o nome de variável `MCP_ALLOW_INSECURE_URLS`, o trecho literal
  // “user:password@” e o caminho `/mcp` — os quatro primeiros por D-15, os outros porque são
  // identificadores e não prosa (D-17).

  'systems.hint_url_not_absolute': () => 'Use uma URL absoluta, com https://.',
  'systems.hint_url_has_credentials': () =>
    'Tire a parte “user:password@” e cadastre a credencial como uma referência de token.',
  'systems.hint_url_plaintext': () =>
    'HTTP em texto puro exporia o bearer token em trânsito. Use https, ou defina MCP_ALLOW_INSECURE_URLS=true apenas para desenvolvimento local.',
  'systems.hint_url_transport': () => 'Os únicos transportes aceitos são https (e http em desenvolvimento).',
  'systems.hint_host_unresolved': () =>
    'Confira a grafia, e se esta instalação enxerga a zona DNS que responde por esse nome.',
  'systems.hint_address_private': () =>
    'Cadastre um endpoint externo roteável, ou coloque o sistema atrás de um proxy reverso com um nome público.',
  'systems.hint_address_resolves_private': () =>
    'A plataforma não disca para endereços internos. Publique o sistema em um endpoint roteável.',
  'systems.hint_url_invalid': () => 'Corrija o endereço deste sistema conectado.',
  'systems.hint_ssrf_blocked': () => 'Esta plataforma não disca para endereços privados, de loopback ou link-local.',
  'systems.hint_token_unresolved': () =>
    'O cadastro guarda uma referência, não um token. Defina a variável de ambiente referenciada (ou configure o armazenamento de credenciais) e tente de novo.',
  'systems.hint_connect': () =>
    'Não foi possível alcançar o endpoint. Confira DNS, TLS e a política de egress — esta é uma falha de rede, não de protocolo.',
  'systems.hint_version_mismatch': () =>
    'Não há nenhuma revisão de protocolo em comum. Atualize o sistema conectado, ou espere até que esta plataforma passe a falar a revisão dele.',
  'systems.hint_authorization_required': () =>
    'O sistema conectado exige uma credencial que não recebeu. Conecte uma conta, ou informe uma referência de token.',
  'systems.hint_sign_in_with_account': () =>
    'Este sistema conectado emite credenciais por meio de um authorization server. Conecte uma conta em vez de colar um token.',
  'systems.hint_discover': () =>
    'O endpoint respondeu, mas não como um sistema conectado. Confirme que esta URL exata é o endpoint do sistema, e não uma página de documentação ou um caminho base.',
  'systems.hint_not_an_endpoint': () =>
    'Esse endereço respondeu, mas não como endpoint de um sistema conectado. Em geral é uma página de documentação ou a raiz do site — o endpoint costuma ficar em um caminho como /mcp.',
  'systems.hint_tools_list': () =>
    'O sistema conectado foi descoberto sem problemas, mas não conseguiu listar as ferramentas dele. Confira os escopos da credencial.',

  // --- its capabilities ----------------------------------------------------

  'systems.capability_not_found': () => 'Nenhum sistema conectado tem uma capacidade com esse id.',
  'systems.capability_quarantined': () =>
    'Esta capacidade está em quarentena depois de uma mudança de schema. Revise o novo schema e tire a ' +
    'quarentena antes de ligá-la.',
  'systems.tool_arguments_rejected': ({ tool }) => `Esses argumentos não são os que “${tool}” declarou aceitar.`,

  // --- connecting an account to one ----------------------------------------

  'systems.sign_in_not_configured': ({ name }) =>
    `“${name}” não informou qual autoridade emite as credenciais dele, então ainda não há onde entrar. ` +
    'Verifique o sistema primeiro.',
  // Entrada já concluída e entrada expirada são `auth.sign_in_not_pending` e
  // `auth.sign_in_expired` — as mesmas duas frases que o fluxo de pastas conectadas diz.
  'systems.sign_in_incomplete': () => 'Esta entrada não voltou completa. Comece de novo.',
  'systems.sign_in_refused': ({ issuer, error }) => `${issuer} recusou a entrada (${error}).`,
  'systems.connection_not_found': () => 'Você não tem nenhuma conexão com esse id.',
  'systems.service_account_not_connected': () =>
    'Nenhuma conta está conectada a este sistema ainda. Uma pessoa administradora faz essa conexão uma vez, ' +
    'para toda a instalação.',
  'systems.service_account_expired': () =>
    'A conta conectada a este sistema expirou e não pode ser renovada automaticamente. Uma pessoa ' +
    'administradora precisa conectá-la de novo.',
  'systems.service_account_requires_admin': () =>
    'Conectar uma conta que toda a instalação usa exige o papel de administrador.',
  'systems.your_account_not_connected': () => 'Você ainda não conectou sua conta a este sistema.',
  'systems.your_connection_expired': () => 'Sua conexão com este sistema expirou. Conecte de novo.',
  'systems.needs_a_person': ({ name }) =>
    `“${name}” funciona com a conta de cada pessoa, e esta chamada não tem ninguém para ser.`,
  'systems.credential_reference_missing': ({ name }) =>
    `“${name}” espera uma credencial, mas não tem nenhuma referência a uma.`,

  // --- a remote agent ------------------------------------------------------

  'systems.remote_agent_not_found': () => 'Nenhum agente remoto com esse id está registrado.',
  'systems.remote_agent_slug_taken': ({ slug }) => `Já existe um agente remoto registrado com o slug “${slug}”.`,
  'systems.remote_agent_changed': () => 'Outra pessoa mudou este agente remoto. Recarregue e refaça sua edição.',
  'systems.remote_agent_card_missing': () =>
    'Este agente ainda não tem um card obtido. Atualize o card antes de ligá-lo.',
  'systems.remote_agent_card_changed': () =>
    'O card deste agente mudou depois que você revisou. Revise o card atual antes de ligá-lo.',
  'systems.remote_agent_no_interface': () =>
    'O card deste agente não anuncia nenhuma interface HTTP+JSON, então a plataforma não tem como chamá-lo.',

  // --- a runtime policy ----------------------------------------------------

  'systems.policy_not_found': () => 'Essa política não existe mais.',
  'systems.policy_changed': () => 'Outra pessoa mudou esta política. Recarregue e refaça sua alteração.',
  'systems.policy_name_taken': () => 'Outra política já usa esse nome. Escolha um diferente.',
  'systems.policy_not_written': () => 'A política não foi gravada. Tente de novo.',
  'systems.policy_settings_invalid': ({ name }) => `As configurações guardadas de “${name}” não são válidas.`,
  'systems.policy_still_assigned': ({ count, agents }) =>
    plural(count, {
      one: `${count} agente ainda usa esta política (${agents}). Mova esse agente para outra política primeiro.`,
      other: `${count} agentes ainda usam esta política (${agents}). Mova esses agentes para outra política primeiro.`
    }),
  'systems.builtin_policy_keeps_its_name': ({ name }) =>
    `“${name}” é a política interna e mantém o nome. Copie para uma política nova se quiser uma com outro nome.`,
  'systems.builtin_policy_not_removable': ({ name }) =>
    `“${name}” é a política interna e não pode ser removida. Todos os agentes recorrem a ela.`,
  'systems.policy_agent_not_configured': ({ agent }) => `Nenhum agente chamado “${agent}” está configurado.`,
  'systems.policy_assignment_not_written': () => 'A atribuição de política não foi gravada. Tente de novo.'
}
